import { fmt, parseKip } from '@k2pos/core';
import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { Field } from './Field';
import { Mono, Text } from './Text';
import { colors } from '@/theme/tokens';
import styles from './CloseDaySheet.module.css';

interface CloseDaySheetProps {
  open: boolean;
  /** Cash that should be in the drawer: cash sales plus debt collected. */
  expected: number;
  busy?: boolean;
  onConfirm: (counted: number | null) => void;
  onClose: () => void;
}

/**
 * The end-of-day count.
 *
 * Counting is optional — a shop in a hurry can close without it — but the
 * variance is the number that catches a mis-keyed sale or a short till, so the
 * sheet asks for it rather than closing silently. `null` records honestly that
 * nobody counted, instead of a zero nobody stands behind.
 *
 * Same `<dialog>` shape as QtySheet: focus trap, Esc and `::backdrop` for free,
 * and supported at the Chrome 90 floor.
 */
export function CloseDaySheet({
  open,
  expected,
  busy = false,
  onConfirm,
  onClose,
}: CloseDaySheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setDraft('');
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (!open) return null;

  const counted = parseKip(draft);
  const variance = counted === null ? null : counted - expected;

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className={styles.sheet}>
        <div className={styles.grabber} />

        <Text weight={800} size={16}>
          ນັບເງິນ ແລະ ປິດຍອດ
        </Text>

        <div className={styles.row}>
          <Text size={12.5} color={colors.textMuted} className="grow">
            ຄວນມີໃນລິ້ນຊັກ
          </Text>
          <Mono weight={700} size={15}>
            {fmt(expected)} ₭
          </Mono>
        </div>

        <div className={styles.field}>
          <Text weight={700} size={11} color={colors.textMuted}>
            ນັບໄດ້ຈິງ · ບໍ່ບັງຄັບ
          </Text>
          <Field
            value={draft}
            onChange={setDraft}
            kind="kip"
            placeholder={String(expected)}
            label="ເງິນສົດທີ່ນັບໄດ້"
            autoFocus
          />
        </div>

        {variance !== null && variance !== 0 ? (
          <div className={styles.row}>
            <Text size={12.5} weight={700} color={colors.skyDeep} className="grow">
              {variance > 0 ? 'ເກີນ' : 'ຂາດ'}
            </Text>
            <Mono weight={700} size={15} color={colors.skyDeep}>
              {variance > 0 ? '+' : '−'}
              {fmt(Math.abs(variance))} ₭
            </Mono>
          </div>
        ) : null}

        <Text size={10.5} color={colors.textFaint}>
          ປິດຍອດແລ້ວ ຕົວເລກມື້ນີ້ຈະເລີ່ມນັບໃໝ່ · ລາຍການທີ່ຂາຍໄປແລ້ວຍັງຢູ່ຄົບ
        </Text>

        <div className={styles.actions}>
          <Button label="ຍົກເລີກ" variant="outline" className="grow" onPress={onClose} />
          <Button
            label={busy ? 'ກຳລັງບັນທຶກ...' : 'ປິດຍອດ'}
            className="grow"
            disabled={busy}
            onPress={() => onConfirm(counted)}
          />
        </div>
      </div>
    </dialog>
  );
}
